import React, { useState } from "react";
import axios from "axios";
import { Copy, Check, Loader2, KeyRound } from "lucide-react";
import { adminAuthHeader } from "@/hooks/useAdminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Button shown on every lead that hasn't set a password yet. On click:
 *   1. POSTs to /api/admin/users/:userId/password-link
 *   2. Copies the returned magic URL to the clipboard
 *   3. Briefly shows "Copiato!" feedback
 * Used in: AdminFunnelDashboardPage (funnel table) + AdminUserDetailPage (header card).
 */
export default function CopyPasswordLinkButton({ userId, variant = "table", onError }) {
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [errorMsg, setErrorMsg] = useState("");

  const handle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setState("loading");
    setErrorMsg("");
    try {
      const { data } = await axios.post(
        `${API}/admin/users/${userId}/password-link`,
        {},
        { headers: adminAuthHeader() },
      );
      try {
        await navigator.clipboard.writeText(data.url);
      } catch {
        // Fallback: legacy browsers without Clipboard API
        const ta = document.createElement("textarea");
        ta.value = data.url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setState("done");
      setTimeout(() => setState("idle"), 2200);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Errore nella generazione del link.";
      setErrorMsg(typeof detail === "string" ? detail : "Errore.");
      setState("error");
      if (onError) onError(detail);
      setTimeout(() => setState("idle"), 3000);
    }
  };

  const isInline = variant === "table";
  const base = isInline
    ? "inline-flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1.5 rounded-full border transition"
    : "inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-full border transition";

  if (state === "done") {
    return (
      <button type="button" data-testid={`copy-password-link-${userId}`}
        className={`${base} border-emerald-200 bg-emerald-50 text-emerald-700`}>
        <Check size={isInline ? 12 : 14} /> Copiato!
      </button>
    );
  }
  if (state === "error") {
    return (
      <button type="button" onClick={handle} data-testid={`copy-password-link-${userId}`}
        title={errorMsg}
        className={`${base} border-red-200 bg-red-50 text-red-700`}>
        Riprova
      </button>
    );
  }

  return (
    <button type="button" onClick={handle} disabled={state === "loading"}
      data-testid={`copy-password-link-${userId}`}
      title="Copia link per impostare/reset password (scade in 14 giorni)"
      className={`${base} border-[#dbe3ec] bg-white hover:border-[var(--np-secondary)] hover:text-[var(--np-secondary)] disabled:opacity-60`}
      style={{ color: "var(--np-primary)" }}>
      {state === "loading"
        ? <><Loader2 className="animate-spin" size={isInline ? 12 : 14} /> Genero…</>
        : <><KeyRound size={isInline ? 12 : 14} /> {isInline ? "Reset password" : "Copia link reset password"}</>}
      {!isInline && <Copy size={13} />}
    </button>
  );
}
